import { useState, useEffect, useCallback, useRef } from 'react';
import {
  saveCampaignDraft,
  loadCampaignDraft,
  removeCampaignDraft,
  draftExists,
  getCreateDraftKey,
  getEditDraftKey,
  autoSaveDraft,
  validateDraftData,
  cleanupOldDrafts,
} from '~/utils/campaignDraftManager';

/**
 * Custom hook for managing campaign drafts with auto-save functionality
 */
const useCampaignDrafts = (campaignId = null, isEditMode = false) => {
  const [hasDraft, setHasDraft] = useState(false);
  const [draftData, setDraftData] = useState(null);
  const [isAutoSaving, setIsAutoSaving] = useState(false);
  const [showDraftAlert, setShowDraftAlert] = useState(false);

  // Ref to track if component is mounted
  const isMountedRef = useRef(true);

  // Ref to track current draft state to avoid unnecessary saves
  const lastSavedDataRef = useRef(null);

  // Get the appropriate storage key
  const getDraftKey = useCallback(() => {
    return isEditMode ? getEditDraftKey(campaignId) : getCreateDraftKey();
  }, [campaignId, isEditMode]);

  // Check for existing draft on mount
  useEffect(() => {
    const checkForExistingDraft = () => {
      const draftKey = getDraftKey();

      if (draftExists(draftKey)) {
        const existingDraft = loadCampaignDraft(draftKey);

        if (existingDraft && validateDraftData(existingDraft)) {
          setDraftData(existingDraft);
          setHasDraft(true);
          setTimeout(() => {
            setShowDraftAlert(true);
          }, 100);
        } else {
          // Invalid draft data, remove it
          removeCampaignDraft(draftKey);
        }
      }

      // Clean up old drafts periodically
      cleanupOldDrafts(7); // Remove drafts older than 7 days
    };

    if (isMountedRef.current) {
      checkForExistingDraft();
    }
  }, [getDraftKey]);

  // Auto-save function with debouncing
  const saveDraft = useCallback(
    (campaignData) => {
      const draftKey = getDraftKey();

      console.log('saveDraft called:', {
        draftKey,
        campaignData: {
          campaignName: campaignData.campaignName,
          description: campaignData.description,
          stepsCount: campaignData.steps?.length || 0,
        },
      });

      // Only save if data has actually changed
      const dataString = JSON.stringify(campaignData);
      if (lastSavedDataRef.current === dataString) {
        console.log('Data unchanged, skipping save');
        return;
      }

      console.log('Data changed, proceeding with save to:', draftKey);
      setIsAutoSaving(true);

      autoSaveDraft(draftKey, campaignData, 1000);
      lastSavedDataRef.current = dataString;

      // Reset auto-saving indicator after a short delay
      setTimeout(() => {
        if (isMountedRef.current) {
          setIsAutoSaving(false);
        }
      }, 1500);
    },
    [getDraftKey]
  );

  // Manual save function (immediate save)
  const saveDraftImmediately = useCallback(
    (campaignData) => {
      const draftKey = getDraftKey();
      const success = saveCampaignDraft(draftKey, campaignData);

      if (success) {
        lastSavedDataRef.current = JSON.stringify(campaignData);
        setHasDraft(true);
      }

      return success;
    },
    [getDraftKey]
  );

  // Remove draft
  const clearDraft = useCallback(() => {
    const draftKey = getDraftKey();
    const success = removeCampaignDraft(draftKey);

    if (success) {
      setHasDraft(false);
      setDraftData(null);
      lastSavedDataRef.current = null;
    }

    return success;
  }, [getDraftKey]);

  // Load draft data
  const loadDraft = useCallback(() => {
    const draftKey = getDraftKey();
    const data = loadCampaignDraft(draftKey);

    if (data && validateDraftData(data)) {
      return data;
    }

    return null;
  }, [getDraftKey]);

  // Handle draft recovery decisions
  const handleContinueEditing = useCallback(() => {
    setTimeout(() => {
      setShowDraftAlert(false);
    }, 100);
    // Draft data is already loaded, caller can use loadDraft() to get it
    return draftData;
  }, [draftData]);

  const handleStartFresh = useCallback(() => {
    clearDraft();
    setShowDraftAlert(false);
    return null;
  }, [clearDraft]);

  const handleCancelDraftRecovery = useCallback(() => {
    clearDraft();
    setShowDraftAlert(false);
    return 'cancel'; // Signal to caller that user wants to exit
  }, [clearDraft]);

  // Handle final save (remove draft)
  const handleFinalSave = useCallback(() => {
    return clearDraft();
  }, [clearDraft]);

  // Auto-save effect with state monitoring
  const setupAutoSave = useCallback(
    (getStateFunction = []) => {
      let autoSaveInterval;

      if (typeof getStateFunction === 'function') {
        autoSaveInterval = setInterval(() => {
          const currentState = getStateFunction();

          // Only auto-save if there's meaningful content
          if (
            currentState &&
            (currentState.campaignName?.trim() ||
              currentState.description?.trim() ||
              (currentState.steps && currentState.steps.length > 0))
          ) {
            console.log('Auto-save interval triggered');
            saveDraft(currentState);
          }
        }, 2000); // Auto-save every 2 seconds (reduced from 5 seconds)
      }

      return () => {
        if (autoSaveInterval) {
          clearInterval(autoSaveInterval);
        }
      };
    },
    [saveDraft]
  );

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Enhanced draft detection for concurrent editing
  const checkForConflicts = useCallback(() => {
    const draftKey = getDraftKey();
    const currentDraft = loadCampaignDraft(draftKey);

    if (currentDraft && draftData) {
      // Compare timestamps to detect if another tab/window has updated the draft
      const currentTime = new Date(currentDraft.lastModified);
      const storedTime = new Date(draftData.lastModified);

      return currentTime > storedTime;
    }

    return false;
  }, [getDraftKey, draftData]);

  return {
    // State
    hasDraft,
    draftData,
    isAutoSaving,
    showDraftAlert,

    // Actions
    saveDraft,
    saveDraftImmediately,
    clearDraft,
    loadDraft,

    // Draft recovery handlers
    handleContinueEditing,
    handleStartFresh,
    handleCancelDraftRecovery,
    handleFinalSave,

    // Utilities
    setupAutoSave,
    checkForConflicts,

    // Setters for external control
    setShowDraftAlert,

    // Computed values
    draftKey: getDraftKey(),
  };
};

export default useCampaignDrafts;
